import { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import initApi from '@/api/initApiV2';

import {
  OrganizationRisksApi,
  type HighestRisksPerCategory,
  type OrganizationRisk,
  type OrganizationRiskCategory,
  type OrganizationRiskCategoryPaginated,
  type OrganizationRiskExpandedPaginated,
  type OrganizationRiskPaginated,
  type OrganizationRisksApiAddOrganizationRiskThreatsToOrganizationRiskRequest,
  type OrganizationRisksApiArchiveOrganizationRiskCategoryRequest,
  type OrganizationRisksApiArchiveOrganizationRiskRequest,
  type OrganizationRisksApiArchiveOrganizationRiskThreatRequest,
  type OrganizationRisksApiCreateOrganizationRiskCategoryRequest,
  type OrganizationRisksApiCreateOrganizationRiskRequest,
  type OrganizationRisksApiCreateOrganizationRiskThreatRequest,
  type OrganizationRisksApiDeleteOrganizationRiskThreatRequest,
  type OrganizationRisksApiGetOrganizationRiskCategoriesRequest,
  type OrganizationRisksApiGetOrganizationRiskCategoryRequest,
  type OrganizationRisksApiGetOrganizationRiskPortfolioProjectRequest,
  type OrganizationRisksApiGetOrganizationRiskPortfolioRequest,
  type OrganizationRisksApiGetOrganizationRiskPortfolioRiskCategoryRequest,
  type OrganizationRisksApiGetOrganizationRiskRequest,
  type OrganizationRisksApiGetOrganizationRisksExpandedRequest,
  type OrganizationRisksApiGetOrganizationRisksRequest,
  type OrganizationRisksApiGetOrganizationRiskThreatRequest,
  type OrganizationRisksApiGetOrganizationRiskThreatsRequest,
  type OrganizationRisksApiGetRiskThreatsFromOrganizationRisksRequest,
  type OrganizationRisksApiUpdateOrganizationRiskCategoryRequest,
  type OrganizationRisksApiUpdateOrganizationRiskRequest,
  type OrganizationRisksApiUpdateOrganizationRiskThreatRequest,
  type OrganizationRisksOverview,
  type OrganizationRiskThreat,
  type OrganizationRiskThreatPaginated,
  type ProjectByRiskValue,
  type RiskPortfolioOverview,
  type RiskPortfolioProject,
  type RiskPortfolioRiskCategory,
  type RiskStatusByCategory,
} from '@/clients/v2';

import { handleApiError } from '@/api/handleApiError.ts';
import type { RequestConfig } from '@/api/Request';

export const useOrganizationRisks = defineStore('organization-risks', () => {
  const organizationOverviewData = ref<OrganizationRisksOverview | null>(null);
  const organizationRisks = ref<OrganizationRiskPaginated | null>(null);
  const organizationRisksExpanded = ref<OrganizationRiskExpandedPaginated | null>(null);
  const organizationRiskCategories = ref<OrganizationRiskCategoryPaginated | null>(null);
  const organizationRiskThreats = ref<OrganizationRiskThreatPaginated | null>(null);
  const organizationRiskStatusByCategoryItems = ref<RiskStatusByCategory[]>([]);
  const organizationTopRiskStatusByCategoryItems = ref<HighestRisksPerCategory[]>([]);
  const organizationTopRisksByValueItems = ref<ProjectByRiskValue[]>([]);
  const currentRisk = ref<OrganizationRisk | null>(null);
  const riskThreats = ref<OrganizationRiskThreat[]>([]);
  const currentCategory = ref<OrganizationRiskCategory | null>(null);
  const currentThreat = ref<OrganizationRiskThreat | null>(null);
  const riskPortfolioOverview = ref<RiskPortfolioOverview | null>(null);
  const riskPortfolioProject = ref<RiskPortfolioProject | null>(null);
  const riskPortfolioRiskCategory = ref<RiskPortfolioRiskCategory | null>(null);

  const getOrganizationRisksOverview = async () => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationRisksOverview();

      if (!response) return;

      organizationOverviewData.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationRisks = async (
    params?: OrganizationRisksApiGetOrganizationRisksRequest,
    options?: RequestConfig,
    reassign = true,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationRisks(params, options);

      if (!response) return;

      if (reassign) {
        organizationRisks.value = response.data;
      }
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationRisksExpanded = async (
    params: OrganizationRisksApiGetOrganizationRisksExpandedRequest,
    options?: RequestConfig,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationRisksExpanded(params, options);

      if (!response) return;

      organizationRisksExpanded.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createOrganizationRiskCategory = async (
    params: OrganizationRisksApiCreateOrganizationRiskCategoryRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.createOrganizationRiskCategory(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationRiskCategory = async (
    params: OrganizationRisksApiGetOrganizationRiskCategoryRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationRiskCategory(params);

      if (!response) return;

      currentCategory.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationRiskCategories = async (
    params?: OrganizationRisksApiGetOrganizationRiskCategoriesRequest,
    options?: RequestConfig,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationRiskCategories(params, options);

      if (!response) return;

      organizationRiskCategories.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateOrganizationRiskCategory = async (
    params: OrganizationRisksApiUpdateOrganizationRiskCategoryRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.updateOrganizationRiskCategory(params);

      if (!response) return;

      currentCategory.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const archiveOrganizationRiskCategory = async (
    params: OrganizationRisksApiArchiveOrganizationRiskCategoryRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.archiveOrganizationRiskCategory(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationRiskStatusByCategory = async (options?: RequestConfig) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationRiskStatusByCategory(options);

      if (!response) return;

      organizationRiskStatusByCategoryItems.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationTopRiskPerCategory = async (options?: RequestConfig) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationTopRiskPerCategory(options);

      if (!response) return;

      organizationTopRiskStatusByCategoryItems.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationTopRiskByValue = async (options?: RequestConfig) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getTopProjectsByRiskValue(options);

      if (!response) return;

      organizationTopRisksByValueItems.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createOrganizationRisk = async (
    params: OrganizationRisksApiCreateOrganizationRiskRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.createOrganizationRisk(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationRisk = async (params: OrganizationRisksApiGetOrganizationRiskRequest) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationRisk(params);

      if (!response) return;

      currentRisk.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateOrganizationRisk = async (
    params: OrganizationRisksApiUpdateOrganizationRiskRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.updateOrganizationRisk(params);

      if (!response) return;

      currentRisk.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const archiveOrganizationRisk = async (
    params: OrganizationRisksApiArchiveOrganizationRiskRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.archiveOrganizationRisk(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getRiskThreatsFromOrganizationRisks = async (
    params: OrganizationRisksApiGetRiskThreatsFromOrganizationRisksRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getRiskThreatsFromOrganizationRisks(params);

      if (!response) return;
      riskThreats.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const addRiskThreatsToOrganizationRisk = async (
    params: OrganizationRisksApiAddOrganizationRiskThreatsToOrganizationRiskRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response =
        await organizationRisksApi?.addOrganizationRiskThreatsToOrganizationRisk(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationRiskThreats = async (
    params?: OrganizationRisksApiGetOrganizationRiskThreatsRequest,
    options?: RequestConfig,
    reassign = true,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationRiskThreats(params, options);

      if (!response) return;

      if (reassign) {
        organizationRiskThreats.value = response.data;
      }
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationRiskThreat = async (
    params: OrganizationRisksApiGetOrganizationRiskThreatRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationRiskThreat(params);

      if (!response) return;

      currentThreat.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createOrganizationRiskThreat = async (
    params: OrganizationRisksApiCreateOrganizationRiskThreatRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.createOrganizationRiskThreat(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateOrganizationRiskThreat = async (
    params: OrganizationRisksApiUpdateOrganizationRiskThreatRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.updateOrganizationRiskThreat(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const archiveOrganizationRiskThreat = async (
    params: OrganizationRisksApiArchiveOrganizationRiskThreatRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.archiveOrganizationRiskThreat(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const deleteOrganizationRiskThreat = async (
    params: OrganizationRisksApiDeleteOrganizationRiskThreatRequest,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.deleteOrganizationRiskThreat(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationRiskPortfolio = async (
    params?: OrganizationRisksApiGetOrganizationRiskPortfolioRequest,
    options?: RequestConfig,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationRiskPortfolio(params, options);

      if (!response) return;

      riskPortfolioOverview.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationRiskPortfolioProject = async (
    params: OrganizationRisksApiGetOrganizationRiskPortfolioProjectRequest,
    options?: RequestConfig,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationRiskPortfolioProject(
        params,
        options,
      );

      if (!response) return;

      riskPortfolioProject.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getOrganizationRiskPortfolioRiskCategory = async (
    params: OrganizationRisksApiGetOrganizationRiskPortfolioRiskCategoryRequest,
    options?: RequestConfig,
  ) => {
    const organizationRisksApi = await initApi(OrganizationRisksApi);

    try {
      const response = await organizationRisksApi?.getOrganizationRiskPortfolioRiskCategory(
        params,
        options,
      );

      if (!response) return;

      riskPortfolioRiskCategory.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const resetStore = () => {
    organizationOverviewData.value = null;
    organizationRiskCategories.value = null;
    organizationRisks.value = null;
    organizationRisksExpanded.value = null;
    organizationRiskThreats.value = null;
    organizationRiskStatusByCategoryItems.value = [];
    organizationTopRiskStatusByCategoryItems.value = [];
    organizationTopRisksByValueItems.value = [];
    riskPortfolioOverview.value = null;
    riskPortfolioProject.value = null;
    riskPortfolioRiskCategory.value = null;
    resetCategory();
    resetRisk();
    resetThreat();
  };

  const resetCategory = () => {
    currentCategory.value = null;
  };

  const resetRisk = () => {
    currentRisk.value = null;
    riskThreats.value = [];
  };

  const resetThreat = () => {
    currentThreat.value = null;
  };

  return {
    organizationOverviewData,
    organizationRisks,
    organizationRisksExpanded,
    currentRisk,
    organizationRiskCategories,
    organizationRiskThreats,
    organizationRiskStatusByCategoryItems,
    organizationTopRiskStatusByCategoryItems,
    organizationTopRisksByValueItems,
    currentCategory,
    riskThreats,
    currentThreat,
    riskPortfolioOverview,
    riskPortfolioProject,
    riskPortfolioRiskCategory,

    getOrganizationRisksOverview,
    getOrganizationRisks,
    getOrganizationRisksExpanded,
    getOrganizationRisk,
    updateOrganizationRisk,
    archiveOrganizationRisk,
    getRiskThreatsFromOrganizationRisks,
    getOrganizationRiskThreats,
    deleteOrganizationRiskThreat,
    getOrganizationRiskCategories,
    updateOrganizationRiskCategory,
    archiveOrganizationRiskCategory,
    getOrganizationRiskCategory,
    getOrganizationRiskStatusByCategory,
    getOrganizationTopRiskPerCategory,
    getOrganizationTopRiskByValue,
    createOrganizationRiskCategory,
    createOrganizationRisk,
    getOrganizationRiskThreat,
    createOrganizationRiskThreat,
    updateOrganizationRiskThreat,
    archiveOrganizationRiskThreat,
    addRiskThreatsToOrganizationRisk,
    getOrganizationRiskPortfolio,
    getOrganizationRiskPortfolioProject,
    getOrganizationRiskPortfolioRiskCategory,

    resetStore,
    resetCategory,
    resetRisk,
    resetThreat,
  };
});
